import Skeleton from '@carvana/ds-skeleton';
import LoadingTile from '@carvana/merch-tile/components/LoadingTile';
import LiveAnnouncer from '@/common/LiveAnnouncer';
import { suggestedFilterChipsSkeletonWidths } from '@/common/SuggestedFilterChips/constants';
import BannerSkeleton from '~/components/srp/Banners/BannerSkeleton';
import { FILTER_DESKTOP_SCROLL_CONTAINER_ID } from '~/components/srp/FiltersMenu/constants';
import styles from '~/components/srp/MainLayout/Filters/Filters.module.css';
import { Aside } from '~/components/srp/MainLayout/Filters/Filters.styles';
import StyledResultsSection from '~/components/srp/ResultsSection/StyledResultSection';

const FilterMenuItem = () => (
  <div className="flex justify-between border-b border-border-subtle px-2xl pb-2xl pt-2xl">
    <Skeleton width="160px" height="24px" />
  </div>
);

type OwnProps = {
  isInPriceDropSuppressionTreatment: boolean;
  shouldShowComposer: boolean;
};

export default function SearchResultsLoadingSkeleton({
  isInPriceDropSuppressionTreatment,
  shouldShowComposer
}: OwnProps) {
  return (
    <div className="grid grid-cols-1 bg-background-default sm:px-3xl sm:pt-2xl md:grid-cols-[292px_1fr] md:gap-3xl md:p-4xl lg:gap-4xl">
      <LiveAnnouncer message="Loading search results" />
      {/* Filter Menu */}
      <Aside
        className={styles.scrollContainer}
        id={FILTER_DESKTOP_SCROLL_CONTAINER_ID}
      >
        {Array.from({ length: 15 }).map((_, x) => (
          <FilterMenuItem key={x} />
        ))}
      </Aside>

      <div>
        {!shouldShowComposer && (
          <>
            {/* Search Bar */}
            <div className="bg-background-primary-subtle px-2xl py-xl sm:rounded-md sm:p-xl md:p-2xl">
              <div className="flex gap-md pb-xl sm:pb-0">
                <Skeleton className="!h-[40px] !w-full sm:!h-[38px] md:!h-[54px]" />
                <Skeleton
                  width="42px"
                  className="!h-[44px] sm:!h-[40px] md:hidden"
                />
              </div>

              {/* Mobile Filter Items */}
              <div className="flex gap-md sm:hidden">
                <Skeleton width="76px" height="34px" />
                <Skeleton width="124px" height="34px" />
                <Skeleton width="112px" height="34px" />
              </div>
            </div>

            {/* Suggested Filters */}
            <div className="bg-background-default px-2xl sm:px-0">
              <div className="no-scrollbar flex overflow-x-auto">
                <div className="flex gap-md pt-xl sm:flex-wrap">
                  {suggestedFilterChipsSkeletonWidths.map((width, index) => (
                    <Skeleton
                      key={index}
                      height="34px"
                      width={`${width}px`}
                      className="shrink-0 rounded-md"
                    />
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="bg-background-default px-2xl sm:px-0">
          {/* Search Tools */}
          <div className="flex justify-between py-2xl sm:py-3xl">
            <Skeleton width="73px" height="20px" />
            <div className="flex items-center gap-lg text-text-weak">
              <Skeleton className="!h-[20px] !w-[53px] sm:!w-[106px]" />
              {'|'}
              <Skeleton className="!h-[20px] !w-[53px] sm:!w-[106px]" />
              {'|'}
              <Skeleton className="!h-[20px] !w-[53px] sm:!w-[106px]" />
              {shouldShowComposer && (
                <>
                  {'|'}
                  <Skeleton className="!h-[20px] !w-[40px]" />
                </>
              )}
            </div>
          </div>

          {/* Promo Banner */}
          {!shouldShowComposer && !isInPriceDropSuppressionTreatment && (
            <BannerSkeleton />
          )}

          {/* Result section */}
          <StyledResultsSection>
            {Array.from({ length: 24 }).map((_, x) => (
              <LoadingTile key={x} />
            ))}
          </StyledResultsSection>
        </div>
      </div>
    </div>
  );
}
